/**
 * composables/zoomable.js
 *
 * Removes the touch listeners `vue-zoomable` leaves on `document`.
 *
 * On `touchstart` the library adds a `touchmove` listener to `document` and
 * removes it only on `touchend`. It never listens to `touchcancel` and never
 * cleans up on unmount. Android cancels touches instead of ending them when the
 * browser takes a gesture over, so the listener stays. After the drawing is
 * gone, the next two-finger touch anywhere in the app runs it against a
 * container that no longer exists and throws
 * `Cannot read properties of null (reading 'getBoundingClientRect')` — seen in
 * Sentry on the QR reader, which is simply the next screen people touch.
 *
 * The library's own `touchend` handler is what removes the listener, so we
 * send it one: on `touchcancel` while the component is mounted, and once more
 * when it unmounts. Call this from `setup()` of every component that renders
 * `<VueZoomable>`.
 */
import { onMounted, onBeforeUnmount } from 'vue'

function releaseZoomableTouch () {
  // Only touch devices can have leaked a touch listener, and those are the
  // ones with a TouchEvent constructor.
  if (typeof TouchEvent === 'undefined') return
  try {
    document.dispatchEvent(new TouchEvent('touchend', { touches: [] }))
  } catch (e) { /* the constructor exists but refuses: nothing to release */ }
}

export function useZoomableTouchCleanup () {
  onMounted(() => document.addEventListener('touchcancel', releaseZoomableTouch))
  onBeforeUnmount(() => {
    document.removeEventListener('touchcancel', releaseZoomableTouch)
    releaseZoomableTouch()
  })
}
